import React from 'react';
import PropTypes from 'prop-types';
import { QuoteProductConfiguration } from '../../../quotes/quoteProductConfiguration';
import QuoteProductDetails from './QuoteProductDetails';
import LeadTimeDetailsModal from './LeadTimeDetailsModal';
import { QuoteProductDto } from '../../../quotes/otherQuoteEntities';
import { ProductConfiguration } from '../../../products/productConfiguration';
import { ProductOptionSearchItem } from '../../../products/productOption';
import {
  SimpleModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@ami/react/core';
import QuoteProductLeadTime from '../../../quotes/lead-times/QuoteProductLeadTime';
import InventoryItemBadge from '../../../quotes/InventoryItemBadge';
import { Alert } from 'reactstrap';
import { Quote } from '../../../quotes/quote';
import MachineBoltPatternType from '../../../machines/machineBoltPatternType';
import { ProductGroupTypes } from '../../../products/productGroup';
import ReviewQuoteProductPricing from '../../review/quote-products/ReviewQuoteProductPricing';
import { appConfig } from '@ami/react/core';

export default class ReviewQuoteProduct extends React.Component {
  constructor(props) {
    super(props);
    this.leadTimeDetailsModalRef = React.createRef();
    this.handleOnEdit = this.handleOnEdit.bind(this);
    this.hideValidationErrorsModal = this.hideValidationErrorsModal.bind(this);
    this.toggle = this.toggle.bind(this);
    this.state = {
      tooltipOpen: false,
      showValidationErrorsModal: false,
    };
  }

  state = {
    originalLeadTimeState: null
  };

  static propTypes = {
    quote: PropTypes.instanceOf(Quote).isRequired,
    quoteProduct: PropTypes.instanceOf(QuoteProductDto).isRequired,
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration).isRequired,
    onQuantityUpdated: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onAddCustomization: PropTypes.func.isRequired,
    onRemoveCustomization: PropTypes.func.isRequired,
    onOverridePricing: PropTypes.func.isRequired,
    onAddOverrideDiscount: PropTypes.func.isRequired,
    onEditOverrideDiscount: PropTypes.func.isRequired,
    onRemoveOverrideDiscount: PropTypes.func.isRequired,
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    msrpClassNames: PropTypes.string.isRequired,
    dNetClassNames: PropTypes.string.isRequired,
    selectedPriceMode: PropTypes.string.isRequired,
    productOptions: PropTypes.arrayOf(PropTypes.instanceOf(ProductOptionSearchItem)).isRequired,
    isEditMode: PropTypes.bool.isRequired,
  };

  componentDidMount() {
    const { quoteProduct } = this.props;
    this.setState({ originalLeadTimeState: quoteProduct.leadTime.state });
  }

  getProductOptions = (quoteProduct) => {
    return quoteProduct.productOptions
      .filter((qpo) => qpo.productOptionId)
      .map((qpo) =>
        this.props.productOptions.find((x) => x.id === qpo.productOptionId)
      );
  };

  toggle() {
    this.setState({ tooltipOpen: !this.state.tooltipOpen });
  }

  handleOnEdit(quoteProductId) {
    const { quoteProduct, onEdit } = this.props;
    if (
      quoteProduct.validationErrors &&
      quoteProduct.validationErrors.length > 0
    ) {
      this.setState({ showValidationErrorsModal: true });
    } else {
      onEdit(quoteProductId);
    }
  }

  hideValidationErrorsModal() {
    this.setState({ showValidationErrorsModal: false });
  }

  updateLeadTimeOverride = (leadTime) => {
    this.props.quoteProduct.leadTime = leadTime;
    this.props.quoteProduct.totalLeadTimeInDays = leadTime.totalLeadTimeInDays;
    this.forceUpdate();
  };

  render() {
    const { originalLeadTimeState } = this.state;

    const {
      quoteProduct,
      selectedPriceMode,
      productConfiguration,
      quoteProductConfiguration,
      msrpClassNames,
      dNetClassNames,
      onQuantityUpdated,
      onRemove,
      onAddCustomization,
      onRemoveCustomization,
      onOverridePricing,
      onAddOverrideDiscount,
      onEditOverrideDiscount,
      onRemoveOverrideDiscount,
      quote,
      isEditMode
    } = this.props;

    const productOptions = this.getProductOptions(quoteProduct);
    const inventoryItem = quoteProduct.inventoryItemId
      ? quoteProductConfiguration.inventoryItems.find(
        (x) => x.id === quoteProduct.inventoryItemId
      )
      : null;

    const highlightRowClass = quote.maskPricingAndLeadTimes && !!quoteProduct.manufacturingInstructions && quoteProduct.requiresReview  && !quoteProduct.isReviewed;
    const maskPricingAndLeadTimes = highlightRowClass && !appConfig.isManagementApp;

    const alertInterferesWithFactoryMount =
      (quoteProduct.groupType === ProductGroupTypes.ProLinkThumb.id ||
        quoteProduct.groupType === ProductGroupTypes.WeldOnThumb.id) &&
      (quote.attachmentPoints.some(
        (x) => x.machineOemThumbMount === 'HasThumbMount'
      ) ||
        quote.attachmentPoints.some(
          (x) =>
            x.boltPattern === MachineBoltPatternType.HasBoltPattern &&
            x.boltOnMount === 'HasOemBoltOnMount'
        ));

    return (
      <React.Fragment>
        <tr className={highlightRowClass ? 'table-warning' : ''}>
          <QuoteProductDetails
            quoteProductId={quoteProduct.id}
            productGroupImageUrl={quoteProduct.groupImageUrl}
            productGroupName={quoteProduct.groupName}
            productGroupDescription={quoteProduct.groupDescription}
            productPartNumber={quoteProduct.partNumber}
            productPartNumberIncomplete={quoteProduct.partNumberIncomplete}
            productOptions={productOptions}
            standaloneComponents={quoteProduct.standaloneComponents}
            customizations={quoteProduct.customizations}
            dimensions={quoteProduct.dimensions}
            marketing={quoteProduct.groupMarketing}
            productConfiguration={productConfiguration}
            notes={quoteProduct.notes}
            paintColor={quoteProduct.paintColor || inventoryItem?.paintColor}
            manufacturingInstructions={quoteProduct.manufacturingInstructions}
            notesFieldName={
              quoteProduct.groupNotesField
                ? quoteProduct.groupNotesField.name
                : null
            }
            onEdit={this.handleOnEdit}
            onRemove={onRemove}
            onAddCustomization={onAddCustomization}
            onRemoveCustomization={onRemoveCustomization}
            onOverridePricing={onOverridePricing}
            quote={quote}
            productDetail={quoteProduct.detail}
            maskPricingAndLeadTimes={maskPricingAndLeadTimes}
          />
          <td data-title="Lead Time" className="lead-time">
            <QuoteProductLeadTime
              leadTime={quoteProduct.leadTime}
              quoteProductTotalLeadTimeInDays={quoteProduct.totalLeadTimeInDays}
              onLeadTimeViewDetailsClick={() => {
                this.leadTimeDetailsModalRef.current.show();
              }}
              maskPricingAndLeadTimes={maskPricingAndLeadTimes}
            />
            {inventoryItem && (
              <InventoryItemBadge inStock={inventoryItem.inStock} isConsignment={inventoryItem.isConsignment} />
            )}
          </td>
          <td className="price-breakdown">
            {quoteProduct.pricing && <ReviewQuoteProductPricing
              pricing={quoteProduct.pricing}
              selectedPriceMode={selectedPriceMode}
              isoCurrencySymbol={quoteProductConfiguration.isoCurrencySymbol}
              msrpClassNames={msrpClassNames}
              dNetClassNames={dNetClassNames}
              quoteProduct={quoteProduct}
              inventoryItem={inventoryItem}
              onQuantityUpdated={onQuantityUpdated}
              onAddOverrideDiscount={onAddOverrideDiscount}
              onEditOverrideDiscount={onEditOverrideDiscount}
              onRemoveOverrideDiscount={onRemoveOverrideDiscount}
              maskPricingAndLeadTimes={maskPricingAndLeadTimes}
            />}
          </td>
        </tr>
        {alertInterferesWithFactoryMount && (
          <tr>
            <td style={{ borderTop: '0px' }}></td>
            <td colSpan="6" style={{ borderTop: '0px' }}>
              <div className="row text-start">
                <Alert color="danger">
                  <i className="fa fa-exclamation-circle fa-lg text-danger" />{' '}
                  Product requires the machine's OEM thumb mount to be removed
                  (if present)
                </Alert>
              </div>
            </td>
          </tr>
        )}
        <LeadTimeDetailsModal
          ref={this.leadTimeDetailsModalRef}
          quoteProduct={quoteProduct}
          quoteId={quote.id}
          quoteProductConfiguration={quoteProductConfiguration}
          isEditMode={isEditMode}
          isOverrideMode={!isEditMode}
          onLeadTimeOverrideUpdated={this.updateLeadTimeOverride}
          originalLeadTimeState={originalLeadTimeState}
        />
        {quoteProduct.validationErrors &&
          quoteProduct.validationErrors.length > 0 && (
            <SimpleModal show={this.state.showValidationErrorsModal} size="md">
              <ModalHeader title="Product Editing Disabled" />
              <ModalBody>
                <p>
                  It is not possible to edit this product due to the errors
                  listed below. If you need to make changes, please consider
                  removing and re-adding this product.
                </p>
                {quoteProduct.validationErrors.map((x, idx) => (
                  <li key={idx}>{x}</li>
                ))}
              </ModalBody>
              <ModalFooter>
                <button
                  className="btn btn-secondary"
                  onClick={this.hideValidationErrorsModal}
                >
                  Close
                </button>
              </ModalFooter>
            </SimpleModal>
          )}
      </React.Fragment>
    );
  }
}
